export interface AuthHeaderDeps {
  /** From Clerk's useAuth(). `undefined` while Clerk is still loading → treated as guest. */
  isSignedIn: boolean | undefined;
  /** Clerk's session-token getter. May throw or resolve null. */
  getToken: () => Promise<string | null>;
  /** Delay between the two token attempts. Defaults to 250ms; tests pass 0. */
  retryDelayMs?: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function tryGetToken(getToken: () => Promise<string | null>): Promise<string | null> {
  try {
    return await getToken();
  } catch {
    return null;
  }
}

/**
 * Resolve the Apollo request headers for the Clerk auth link.
 *
 * - Guests (not signed in) get NO authorization header — public operations must work tokenless.
 * - Signed-in callers get a `Bearer <token>` header; the token is fetched with one retry.
 * - If a signed-in caller cannot obtain a token, we THROW instead of sending an empty header,
 *   so the operation fails visibly (retryable) rather than as a silent 401.
 */
export async function resolveAuthHeaders(
  headers: Record<string, string>,
  { isSignedIn, getToken, retryDelayMs = 250 }: AuthHeaderDeps
): Promise<{ headers: Record<string, string> }> {
  if (!isSignedIn) {
    return { headers };
  }

  let token = await tryGetToken(getToken);
  if (!token) {
    await sleep(retryDelayMs);
    token = await tryGetToken(getToken);
  }

  if (!token) {
    throw new Error("Authentication token unavailable");
  }

  return { headers: { ...headers, authorization: `Bearer ${token}` } };
}
